// @idme/global-nav has no logo slot, so this replaces its private renderLogo().
import { GlobalNav } from '@idme/global-nav';
import {
  ID_ME_MARK_ASPECT,
  ID_ME_MARK_PATHS,
  ID_ME_MARK_VIEWBOX,
} from './ui/idMeMark';
import { selectFragno1 } from '../reducers/fragno1';

const SVG_NS = 'http://www.w3.org/2000/svg';

const BOX = { height: 48, width: 86 };
// The nav's styles expect 13px of space inside the logo; kept on both sides here.
const INSET = 13;
const MARK_WIDTH = BOX.width - 2 * INSET;
const MARK_HEIGHT = MARK_WIDTH / ID_ME_MARK_ASPECT;
const MARK = { x: INSET, y: (BOX.height - MARK_HEIGHT) / 2 };

const svgElement = (name, attributes) => {
  const element = document.createElementNS(SVG_NS, name);
  Object.entries(attributes).forEach(([key, value]) =>
    element.setAttribute(key, value),
  );
  return element;
};

// DOM nodes rather than Lit templates, so this app doesn't import lit itself.
const createLogoBox = (titleId, title) => {
  const logo = svgElement('svg', {
    'aria-labelledby': titleId,
    class: 'global-nav__logo',
    fill: 'none',
    height: BOX.height,
    role: 'img',
    viewBox: `0 0 ${BOX.width} ${BOX.height}`,
    width: BOX.width,
  });

  const titleElement = svgElement('title', { id: titleId });
  titleElement.textContent = title;
  logo.appendChild(titleElement);

  return logo;
};

export const createMark = (titleId, title) => {
  const logo = createLogoBox(titleId, title);

  const mark = svgElement('svg', {
    height: MARK_HEIGHT,
    viewBox: ID_ME_MARK_VIEWBOX,
    width: MARK_WIDTH,
    x: MARK.x,
    y: MARK.y,
  });
  ID_ME_MARK_PATHS.forEach(({ d, evenOdd }) => {
    const path = svgElement('path', { d, fill: 'white' });
    if (evenOdd) {
      path.setAttribute('fill-rule', 'evenodd');
      path.setAttribute('clip-rule', 'evenodd');
    }
    mark.appendChild(path);
  });
  logo.appendChild(mark);

  return logo;
};

export const NAV_LOGO = { FRAGNO1: 'fragno1', LEGACY: 'legacy' };

// The boot decision, so the nav draws its final logo on its first render.
export const selectNavLogo = (state) =>
  selectFragno1(state) ? NAV_LOGO.FRAGNO1 : NAV_LOGO.LEGACY;

const logosByNav = new WeakMap();

// Lit leaves a node alone when it gets the same one back, so each logo is built once.
const cachedLogo = (nav, logoTitleId, create) => {
  const key = nav.logoTitle;
  if (!logosByNav.has(nav)) logosByNav.set(nav, new Map());
  const logos = logosByNav.get(nav);
  const cached = logos.get(logoTitleId);
  if (cached && cached.key === key) return cached.logo;

  const logo = create();
  logos.set(logoTitleId, { key, logo });
  return logo;
};

export const installNavLogo = () => {
  const { renderLogo } = GlobalNav.prototype;
  if (typeof renderLogo !== 'function' || renderLogo.installed) return;

  const choose = function chooseLogo(logoTitleId = 'logo-title') {
    if (this.navLogo !== NAV_LOGO.FRAGNO1) {
      return renderLogo.call(this, logoTitleId);
    }
    return cachedLogo(this, logoTitleId, () =>
      createMark(logoTitleId, this.logoTitle),
    );
  };
  choose.installed = true;
  GlobalNav.prototype.renderLogo = choose;
};
